import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import Search from 'Components/DesignSystem/Search/Search';
import Button from 'Components/DesignSystem/Button/Button';
import DropdownButton from 'Components/DesignSystem/DropdownButton/DropdownButton';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Pagination from 'Components/DesignSystem/Pagination/Pagination';
import Divider from 'Components/DesignSystem/Divider/Divider';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Tooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import EmptyState from 'Components/DesignSystem/EmptyState/EmptyState';
import NoDataIllustration from 'Components/DesignSystem/EmptyState/NoDataIllustration';
import { CREATE, CREATE_ISR, EMPTY_ISR, ISR_NAME, PROMOTE_DATA } from '../ISRConstants';
import { useNavigate, useLocation } from "react-router";
import CreateISRDialog from '../Components/CreateISRDialog';
import ISRProjectTable from './ISRProjectTable';
import { createProject, deleteISR, getISRProjects, pushModelToEnvironment } from './DefineModel/ModelServices/ModalServices';
import { TISRProjects, TUpsertISRProjectDataModelsInput1, TPushToEnvironmentPayload } from './DefineModel/model';
import { TPaginationParams } from 'Pages/GMS/Performance/analytics';
import DeleteProjectModal from '../Components/DeleteProjectModal';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { useISRContext } from '../Hooks/useISRContext';
import { AppContext } from 'Context/AppContext';
import { GearIcon } from "../../../../assests/GmsIcons";
import MerchantSettings from "./MerchantSettings";
import PromotionDialog from "../Components/PromotionDialog";
import { useProductModelDownload } from './useProductModelDownload';
import { UploadModal } from './UploadModal';
import GuideWidgetPanel from '../Components/GuideWidgetPanel';

/** 10000 is the legacy "All" sentinel the backend page-size API expects. */
const PAGE_SIZE_OPTIONS = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
    { value: 10000, label: 'All' },
];

/**
 * The Guide/ISR landing page, header + chrome on the design system (§ 87).
 * The fetch/sort/search/permission wiring is untouched; what changed is the
 * shell: a real h1 (the title was a <label> — the page had no heading at
 * all), DS Buttons, the DS DropdownButton replacing the local 113-line
 * `Pages/Dropdown.tsx`, DS EmptyState replacing `NoDataMessage`, the DS
 * Dropdown + Pagination strip replacing the raw <select> + PaginatorWithApi,
 * and the fetch spinner flowing into the DS Table as its `loading` prop
 * instead of swapping the whole grid for `TableSkeletonElement`.
 */
const ISRHomePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isrContext = useISRContext();
    const [isrProjects, setIsrProjects] = useState<TISRProjects>([]);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [sortBy, setSortBy] = useState<string>('updatedAt');
    const [sortOrder, setSortOrder] = useState<string>('desc');
    const [searchString, setSearchString] = useState<string>('');
    const [searchInput, setSearchInput] = useState<string>('');

    const isGuide = Boolean(location.pathname.includes('/guide'));

    const appData = useContext(AppContext);
    const readPermission = isGuide ? appData.pm.checkPermission("gmsguide_read") : appData.pm.checkPermission("gmsguide_read");
    const updatePermission = isGuide ? appData.pm.checkPermission("gmsguide_update") : appData.pm.checkPermission("gmsguide_update");
    const mappingOverridePermission = appData.pm.checkPermission("gmsguide_override");
    const configurePermission = appData.pm.checkPermission("gmsguide_configure");
    const widgetReadPermission = appData.pm.checkPermission("gmsguide_widgetread");


    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [deleteItem, setDeleteItem] = useState<{ id: string, name: string }>({ id: '', name: '' });

    const [merchantSettings, setMerchantSettings] = useState<any>([]);
    const [merchantSettingsLoading, setMerchantSettingsLoading] = useState<boolean>(true);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [loader, setLoader] = useState<boolean>(false);
    const [showMerchantSettings, setShowMerchantSettings] = useState<boolean>(false);
    const [showPromotionDialog, setShowPromotionDialog] = useState<boolean>(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showWidgetPanel, setShowWidgetPanel] = useState<boolean>(false);
    const { handleDownload, downloadUrl } = useProductModelDownload();

    const isMountedRef = useRef(true);
    const fetchRequestRef = useRef(0);
    useEffect(() => {
        return () => { isMountedRef.current = false; };
    }, []);

    const openDialog = () => setShowDialog(true);
    const closeDialog = () => setShowDialog(false);

    const navigateToMainPage = (name: string, id: string) => {

        // Bug 12453: model names can contain special characters (#, %, spaces, …);
        // encode the name so the route is valid and resolves correctly.
        if (isGuide) {
            navigate(`/gms/guide/main/${encodeURIComponent(name)}/${id}`);
        } else {
            navigate(`/gms/isr/main/${encodeURIComponent(name)}/${id}`);
        }
    }

    const onFieldSubmit = (fieldName: string) => {
        if (!updatePermission) {
            toast.warning('You do not have permission to create ISR project');
            return;
        }
        const isrProject: TUpsertISRProjectDataModelsInput1 = { industry: isrContext?.industryId ?? '', channel: appData?.channelKey, id: '', project: { ISRName: fieldName, dataModels: [], fields: [], productCategories: [], connectors: [], id: '' }, rules: [] };
        createISRProject(isrProject);
    }

    useEffect(() => {
        if ((location.state as any)?.openCreate) {
            openDialog();
            navigate({
  pathname: location.pathname
}, {
  state: {},
  replace: true
});
        }
    }, []);

    useEffect(() => {
        if (!readPermission) {
            navigate("/workspace/module/permission/denied");
            return;
        }
        if (appData?.channelKey && isrContext?.industryId) {
            isrContext?.clearClassData();
            if (configurePermission) {
                fetchMerchantConfig();
            }
        }
    }, [isrContext?.industryId, appData?.channelKey, isGuide]);

    useEffect(() => {
        if (!readPermission) {
            return;
        }
        if (appData?.channelKey && isrContext?.industryId) {
            fetchISRProjects();
        }
        // searchString is intentionally NOT a dependency: the debounced search handler always
        // produces a new paginationParams object, which drives the re-fetch. Including it here
        // would cause a double fetch on each search/clear (React 17 doesn't batch state updates
        // inside the debounce setTimeout, so the two setStates trigger two effect runs).
    }, [isrContext?.paginationParams, isrContext?.industryId, appData?.channelKey, isGuide, sortBy, sortOrder, isrContext?.refreshCounter]);

    // Search — 500ms debounce, resets to page 1 and searches the entire dataset
    const debouncedSearch = useMemo(
        () => debounce((value: string) => {
            setSearchString(value);
            isrContext?.setPaginationParams((prevState: TPaginationParams) => ({ ...prevState, pageNumber: 1 }));
        }, 500),
        []
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        debouncedSearch(value);
    };


    const handleSort = (column: string) => {
        let newSortOrder: string;
        if (sortBy === column) {
            newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            newSortOrder = column === 'updatedAt' ? 'desc' : 'asc';
        }
        setSortBy(column);
        setSortOrder(newSortOrder);
        isrContext?.setPaginationParams((prevState: TPaginationParams) => ({ ...prevState, pageNumber: 1 }));
    };

    const handlePageChange = (pageNumber: number) => {
        isrContext?.setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageNumber: pageNumber,
        }));
    }

    const updatePageSize = (pageSize: number) => {
        isrContext?.setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageSize: pageSize,
            pageNumber: 1
        }));
    };

    const createISRProject = async (isrProject: TUpsertISRProjectDataModelsInput1) => {
        try {
            setLoader(true);
            const response = await createProject(isrProject, isGuide);
            if (!isMountedRef.current || response === null) return;
            if (response?.done) {
                toast.success(response?.message ?? '');
                const projectName = response?.project_data?.project?.ISRName ?? '';
                const projectId = response?.project_data?.id ?? '';
                navigateToMainPage(projectName, projectId);
                setShowDialog(false);
            } else {
                toast.error(response?.message ?? '');
            }
        } catch (e) {
            console.log('Error', e);
        } finally {
            if (isMountedRef.current) setLoader(false);
        }
    }

    const fetchISRProjects = async () => {
        const requestId = ++fetchRequestRef.current;
        setShowLoader(true);
        try {
            const responseData = await getISRProjects(isGuide, isrContext?.industryId ?? '', appData?.channelKey, isrContext?.paginationParams, sortBy, sortOrder, searchString);
            if (!isMountedRef.current || fetchRequestRef.current !== requestId) return;
            if (responseData?.done) {
                setTotalRecords(responseData?.total ?? 10);
                setIsrProjects(responseData?.data ?? []);
            } else {
                setIsrProjects([]);
            }
        } catch (error) {
            if (!isMountedRef.current || fetchRequestRef.current !== requestId) return;
            setIsrProjects([]);
            console.log(error, 'Error');
        } finally {
            if (isMountedRef.current && fetchRequestRef.current === requestId) setShowLoader(false);
        }
    }

    const fetchMerchantConfig = async () => {
        if (!appData?.channelKey) {
            setMerchantSettingsLoading(false);
            return;
        }
        try {
            setMerchantSettingsLoading(true);
            const responseData = await isrContext?.getMerchantConfig(appData?.channelKey ?? '');
            if (!isMountedRef.current) return;
            if (responseData?.done === true) {
                setMerchantSettings(responseData.data.sb3_channel_config ?? []);
            } else {
                setMerchantSettings([]);
            }
        } catch (e) {
            if (!isMountedRef.current) return;
            setMerchantSettings([]);
        } finally {
            if (isMountedRef.current) setMerchantSettingsLoading(false);
        }
    }

    const closeDeleteModal = () => {
        setDeleteModal(false);
    };

    const handleDelete = async () => {
        if (!deleteItem?.id) {
            toast.info('Project id is missing');
            return;
        }
        setDeleteLoading(true);
        try {
            const responseData = await deleteISR(deleteItem?.id);
            if (!isMountedRef.current) return;
            if (responseData?.done) {
                toast.success(`Model '${deleteItem?.name}' deleted successfully`);
                isrContext?.setPaginationParams({ pageNumber: 1, pageSize: 10 });
            } else {
                toast.error(responseData?.message || 'Failed to delete model. Please try again.');
            }
        } catch (error) {
            if (!isMountedRef.current) return;
            console.log('Error', error);
            toast.error('Failed to delete model. Please try again.');
        } finally {
            if (isMountedRef.current) {
                setDeleteLoading(false);
                setDeleteModal(false);
            }
        }
    }

    const handlePromoteData = async (categories: []) => {
        setShowPromotionDialog(false);
        if (!isrContext?.industryId) return;
        try {
            isrContext?.setDisableButton(true);
            await isrContext?.promoteProducts(isrContext?.industryId, categories);
        } catch (e) {
            console.log(e);
        } finally {
            isrContext?.setDisableButton(false);
        }
    }

    const handlePushToEnvironment = async (payload: TPushToEnvironmentPayload): Promise<{ done: boolean; message?: string }> => {
        const result = await pushModelToEnvironment(payload);
        if (!result.done && !result.message?.toLowerCase().includes('name')) {
            toast.error(result.message ?? 'Failed to push model to the selected environment');
        }
        return result;
    };

    const updateMerchantConfig = async (merchantConfigData: []) => {
        if (!isrContext?.industryId) return;
        try {
            const updatedValues = merchantConfigData.map((e: any) => ({
                config_attr: e.config_attr,
                value: e.value
            }));
            const responseData = await isrContext?.updateMerchantConfig(appData?.channelKey ?? '', updatedValues);
            if (!isMountedRef.current) return;
            if (responseData?.done) {
                setMerchantSettings(merchantConfigData);
                toast.success('Merchant settings updated successfully');
            }
        } catch (e) {
            console.log(e);
        }
    }

    const channelKey = appData?.channelKey;
    const environmentOptions = (appData?.environmentList ?? [])
        .filter((env: any) => env.key !== channelKey)
        .map((env: any) => ({ value: env.key, label: env.name }));

    const handleOverrideSelect = (optionId: string | number) => {
        if (optionId === 'upload') {
            setShowUploadModal(true);
        } else if (optionId === 'download') {
            if (channelKey) handleDownload(channelKey);
        }
    };

    return (
        <React.Fragment>
            <div id="isr-header" className={`tw-pt-[12px] tw-flex tw-items-center tw-mb-1`}>
                {/* The legacy title read `{isGuide ? 'Guide' : 'Guide'}` — both routes
                    ship the same word, preserved as plain text here. */}
                {/* `tw-text-ink` is load-bearing: the heading-2 variant sets SIZE only,
                    so without it the h1 inherits the ambient Bootstrap near-black —
                    invisible on the dark ground (the 2026-09-02 screenshot). */}
                <Typography variant="display" as="h1" id="isr-title" data-testid="isr-page-title" className="tw-pe-1 tw-text-ink">
                    Guide
                </Typography>
                {configurePermission && !merchantSettingsLoading && merchantSettings.length > 0 && (
                    /* § 264 — the glyph is unlabelled, so its only name was the
                       `aria-label` a pointer user never hears. The bubble says
                       "Settings" because that is the word on the panel it opens
                       (`MerchantSettings` ariaLabel), not a second name for it.
                       A bare block comment, not a braced JSX one: this is
                       expression position inside `&& ( … )`, where a JSX comment
                       would be a second expression and not a comment at all. */
                    <Tooltip content="Settings" placement="bottom" data-testid="merchant-settings-tip">
                        <Button
                            variant="subtle"
                            size="sm"
                            aria-label="Settings"
                            data-testid="pages-isrhomepage-button-1"
                            id="merchant-settings-btn"
                            onClick={() => { setShowMerchantSettings(!showMerchantSettings); }}
                        >
                            {/* GmsIcons hardcodes stroke="#0747A6" on the gear — the dim
                                navy in the dark screenshot. A stylesheet rule outranks an
                                SVG presentation attribute, so stroke-current re-inks it
                                here without touching the shared icon file. */}
                            <span aria-hidden="true" className="tw-text-accent [&_path]:tw-stroke-current">
                                <GearIcon />
                            </span>
                        </Button>
                    </Tooltip>
                )}
                {/* § 264 — the search sits with the TITLE, not with the actions. It
                    filters the table below, so it belongs to "what am I looking at",
                    and the header had 322px of dead space here while five controls
                    queued at a uniform 8px pitch in the right two-thirds. Note the
                    DOM order is unchanged: only the flex spacer moved, from before
                    this block to after it, so tab order is exactly what it was. */}
                <div id="guide-model-search" className='tw-w-72 tw-min-w-0 tw-ms-4'>
                    <Search
                        placeholder="Search Models"
                        value={searchInput}
                        onChange={handleSearchChange}
                        /* § 288 — `showLoader` already drives the Table's own `loading`;
                           the inequality adds the 500ms debounce in front of the fetch,
                           so the field is busy for the whole wait rather than the tail
                           of it. */
                        loading={showLoader || searchInput !== searchString}
                        loadingLabel="Searching models"
                        size="md"
                    />
                </div>
                <div className='tw-flex tw-flex-grow' />
                {/* § 264/§ 272 — Widget is the QUIET rung (`subtle`, § 65/§ 181's
                    "toolbar trigger"): it opens a reference panel and does nothing to
                    the data. It is alone in this zone since § 272 moved the override
                    across the break, so it carries no trailing margin — the action
                    group's own 24px is the only gap that follows it. */}
                {isGuide && widgetReadPermission && (
                    <Button
                        variant="subtle"
                        data-testid="pages-isrhomepage-button-2"
                        id="widget-panel-btn"
                        onClick={() => setShowWidgetPanel(true)}
                    >
                        Widget
                    </Button>
                )}
                <UploadModal
                    id={`product-upload-modal`}
                    showModal={showUploadModal}
                    closeModal={() => setShowUploadModal(false)}
                    channelKey={channelKey}
                />
                {/* Hidden iframe for download */}
                <iframe
                    style={{ display: 'none' }}
                    src={downloadUrl || undefined}
                    id="iframe_product_model_download"
                    title="product_model_download"
                    data-testid={`download-iframe`}
                />
                {/* § 264 — 24px, not the row's 8px pitch: proximity is what separates
                    the page utilities from the actions on the data. It lives on THIS
                    group rather than on the override's margin so the break survives
                    when either utility is permission-gated away. `shrink-0` keeps the
                    two CTAs off the list of things that squash first — the search
                    absorbs a narrow viewport instead. */}
                <div id="header-actions" className='tw-flex tw-justify-end tw-items-center tw-gap-2 tw-ms-6 tw-shrink-0'>
                    {/* § 272 — the override moves ACROSS the break and onto the outlined
                        rung, matching PDW's `Download`. § 264 had it beside Widget as a
                        "page utility", which reads the mechanism (it opens a menu) rather
                        than the job: Upload replaces the product-model mapping and
                        Download exports it, so it operates on the data exactly as
                        Promote Data does. Reported by eye across the two headers —
                        borderless here, bordered on PDW, for the same kind of control.

                        Both surfaces now read the same: one quiet utility, a 24px break,
                        then the data operations. DOM order is unchanged (Widget,
                        override, Promote Data, Create), so tab order is what it was —
                        only the group boundary moved. */}
                    {mappingOverridePermission && <DropdownButton
                        label="Product-model override"
                        variant="secondary"
                        align="right"
                        options={[
                            { id: 'upload', label: 'Upload' },
                            { id: 'download', label: 'Download' },
                        ]}
                        onSelect={handleOverrideSelect}
                        data-testid="product-model-override"
                    />}
                    {/* § 264 — `secondary`, not a second fill. Two filled navy blocks
                        side by side meant the header named no single destination; the
                        fill now belongs to Create alone. Promote Data is the rarer and
                        the more consequential of the two (it writes to an environment),
                        which is the one that should not be the most inviting thing on
                        the page. Same handler, same dialog, same permission. */}
                    <Button
                        variant="secondary"
                        data-testid="pages-isrhomepage-button-3"
                        id="promote-data-btn"
                        disabled={isrContext?.disableButton}
                        onClick={() => setShowPromotionDialog(true)}
                    >
                        {PROMOTE_DATA}
                    </Button>
                    <Button
                        variant="primary"
                        data-testid="pages-isrhomepage-button-4"
                        id="create-isr-btn"
                        onClick={openDialog}
                    >
                        {CREATE}
                    </Button>
                </div>
            </div>
            <Divider data-testid="isr-header-divider" />
            {!showLoader && isrProjects.length === 0 ?
                <div id="empty-state" className='tw-flex tw-justify-center tw-mt-20'>
                    <EmptyState
                        id="isr-no-data"
                        title={EMPTY_ISR}
                        illustration={<NoDataIllustration />}
                        action={
                            <Button variant="primary" onClick={openDialog} data-testid="isr-empty-create-btn">
                                {CREATE}
                            </Button>
                        }
                        data-testid="isr-empty-state"
                    />
                </div> : null}
            {showLoader || isrProjects?.length > 0 ?
                <ISRProjectTable
                    isrProjects={isrProjects}
                    loading={showLoader}
                    setDeleteModal={setDeleteModal}
                    setDeleteItem={setDeleteItem}
                    isGuide={isGuide}
                    updatePermission={updatePermission}
                    fetchISRProjects={fetchISRProjects}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    environmentOptions={environmentOptions}
                    industryId={isrContext?.industryId ?? ''}
                    onPushToEnvironment={handlePushToEnvironment}
                /> : null}
            {!showLoader && isrProjects?.length > 0 ?
                <div id="pagination-container" className="tw-py-2">
                    <div className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-flex-wrap">
                        <Dropdown
                            label=""
                            aria-label="Rows per page"
                            size="sm"
                            className="tw-w-[84px]"
                            options={PAGE_SIZE_OPTIONS}
                            value={isrContext?.paginationParams?.pageSize ?? 10}
                            onChange={(value) => updatePageSize(Number(value))}
                            data-testid="isr-page-size"
                        />
                        <Pagination
                            page={isrContext?.paginationParams?.pageNumber}
                            totalItems={totalRecords}
                            pageSize={isrContext?.paginationParams?.pageSize}
                            onPageChange={handlePageChange}
                            size="sm"
                            data-testid="isr-pagination"
                        />
                    </div>
                </div> : null}
            {showDialog ?
                <CreateISRDialog
                    id={`create-isr`}
                    dialogHeader={CREATE_ISR}
                    labelName={ISR_NAME}
                    onClose={closeDialog}
                    onSubmodelFieldSubmit={onFieldSubmit}
                    showIndicator={loader}
                /> : null}
            {showMerchantSettings ?
                <MerchantSettings
                    merchantSettings={merchantSettings}
                    environmentKey={channelKey ?? ''}
                    onClose={() => setShowMerchantSettings(false)}
                    onSubmit={async (merchantConfigData: []) => {
                        setShowMerchantSettings(false);
                        await updateMerchantConfig(merchantConfigData);
                    }}
                /> : null}
            {showPromotionDialog ?
                <PromotionDialog
                    onClose={() => setShowPromotionDialog(false)}
                    onFieldSubmit={(categories: []) => handlePromoteData(categories)}
                /> : null}
            <DeleteProjectModal
                showModal={deleteModal}
                closeModal={closeDeleteModal}
                loading={deleteLoading}
                onConfirm={handleDelete}
                deleteItem={deleteItem}
            />
            {showWidgetPanel && isGuide && isrContext?.industryId && (
                <GuideWidgetPanel
                    industry={isrContext.industryId}
                    channel={appData?.channelKey ?? ''}
                    onClose={() => setShowWidgetPanel(false)}
                    onProjectListRefresh={fetchISRProjects}
                />
            )}
        </React.Fragment>
    );
}

export default ISRHomePage;
